import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { AuthToken } from '../models/auth-token';
import { AuthTokenStorage } from './auth-token-storage.service';
import { UserContext } from '../models/user-context';
import { UserContextStorage } from './user-context.storage';
import { blank, isNil, once } from '@web/utils';
import { AuthApiService } from '../api/services/auth-api.service';
import { SignInRequest } from '../api/requests/sign-in.request';
import { FacebookService } from './facebook.service';
import { RemindPasswordForm } from '../models/remind-password-form';
import { RemindPasswordRequest } from '../api/requests/remind-password.request';
import { ResetPasswordForm } from '../models/reset-password-form';
import { ResetPasswordRequest } from '../api/requests/reset-password.request';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApi = inject(AuthApiService);
  private readonly facebook = inject(FacebookService);
  private readonly tokenStorage = inject(AuthTokenStorage);
  private readonly userContextStorage = inject(UserContextStorage);

  public signInByCredentials(
    usernameOrEmail: string,
    password: string,
    rememberMe = false,
  ): Observable<void> {
    const request = new SignInRequest(usernameOrEmail, password, rememberMe);

    return this.authApi.signInByCredentials(request).pipe(
      map(({ token }) => new AuthToken(token)),
      tap((context) => this.tokenStorage.store(context)),
      blank(),
    );
  }

  public signInByFacebook(): Observable<void> {
    return this.facebook.login().pipe(blank());
  }

  public signOut(): Observable<void> {
    return this.authApi.signOut().pipe(
      tap(() => {
        this.tokenStorage.clear();
        this.userContextStorage.clear();
      }),
      catchError(() => {
        this.tokenStorage.clear();
        this.userContextStorage.clear();

        return of(void 0);
      }),
    );
  }

  public isLoggedIn(): Observable<boolean> {
    return this.userContextStorage.fetch().pipe(
      map((context) => !isNil(context)),
      once(),
    );
  }

  public isNotLoggedIn(): Observable<boolean> {
    return this.userContextStorage.fetch().pipe(
      map((context) => isNil(context)),
      once(),
    );
  }

  public refreshToken(): Observable<AuthToken> {
    return this.authApi.refreshToken().pipe(
      map((response) => new AuthToken(response.token)),
      tap((token) => this.tokenStorage.store(token)),
    );
  }

  public fetchUserContext(): Observable<UserContext> {
    return this.authApi.me().pipe(
      map(
        (response) =>
          new UserContext({
            avatar: response.avatar,
            email: response.email,
            firstName: response.firstName,
            displayName: response.displayName,
            id: response.id,
            lastName: response.lastName,
            permissions: response.permissions,
            username: response.username,
            roles: response.roles,
          }),
      ),
      tap((context) => this.userContextStorage.store(context)),
    );
  }

  public fetchAuthToken(): Observable<AuthToken> {
    return new Observable<AuthToken>((subject) => {
      try {
        const context = this.tokenStorage.fetch();

        subject.next(context);
        subject.complete();
      } catch (error) {
        subject.error(error);
      }
    });
  }

  public remindPassword(form: RemindPasswordForm): Observable<void> {
    return this.authApi.remindPassword(new RemindPasswordRequest(form.email));
  }

  public resetPassword(
    token: string,
    form: ResetPasswordForm,
  ): Observable<void> {
    return this.authApi.resetPassword(
      new ResetPasswordRequest(token, form.password),
    );
  }
}
